// frontend/src/components/VehicleIssuesTab.js
import React, { useState, useEffect } from 'react';
import { 
  AlertCircle, Clock, CheckCircle, XCircle, AlertTriangle, Plus, Edit, Trash2,
  MapPin, Calculator, Phone, Star, ExternalLink, Loader, Shield, X
} from 'lucide-react';
import { vehicleHealthAPI, api } from '../services/api';

const VehicleIssuesTab = ({ vehicleId, vehicleInfo = {}, onIssueUpdate }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [findingMechanicIssue, setFindingMechanicIssue] = useState(null);
  const [estimateIssue, setEstimateIssue] = useState(null);

  useEffect(() => {
    loadIssues();
  }, [vehicleId]);

  const loadIssues = async () => {
    try {
      setLoading(true);
      const issuesData = await vehicleHealthAPI.getVehicleIssues(vehicleId);
      // Ensure issuesData is always an array before setting state
      const issuesArray = Array.isArray(issuesData) ? issuesData : [];
      setIssues(issuesArray);
      setError(null);
    } catch (err) {
      console.error('Failed to load issues:', err);
      setError('Failed to load issues. Please try again.');
      // Ensure issues is set to empty array on error
      setIssues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm('Are you sure you want to delete this issue?')) {
      return;
    }

    try {
      await vehicleHealthAPI.deleteVehicleIssue(issueId);
      await loadIssues(); // Reload the list
      if (onIssueUpdate) {
        onIssueUpdate(); // Notify parent to refresh health data
      }
    } catch (err) {
      console.error('Failed to delete issue:', err);
      alert('Failed to delete issue. Please try again.');
    }
  };

  const handleUpdateIssue = async (updates) => {
    try {
      await vehicleHealthAPI.updateVehicleIssue(selectedIssue.id, updates);
      await loadIssues(); // Reload the list
      setShowEditForm(false);
      setSelectedIssue(null);
      if (onIssueUpdate) {
        onIssueUpdate(); // Notify parent to refresh health data
      }
    } catch (err) {
      console.error('Failed to update issue:', err);
      alert('Failed to update issue. Please try again.');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <Clock className="w-4 h-4 text-warning" />;
      case 'in_progress':
        return <AlertCircle className="w-4 h-4 text-accent" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'closed':
        return <XCircle className="w-4 h-4 text-ink-muted" />;
      default:
        return <Clock className="w-4 h-4 text-ink-muted" />;
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'low': 'bg-info-tint text-info border-info-border',
      'medium': 'bg-warning-tint text-warning border-warning-border',
      'high': 'bg-warning-tint text-warning border-warning-border',
      'critical': 'bg-danger-tint text-danger border-danger-border'
    };
    return colors[severity] || 'bg-surface-muted text-ink-soft border-border-default';
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-warning-tint text-warning border-warning-border',
      'in_progress': 'bg-info-tint text-info border-info-border',
      'resolved': 'bg-success-tint text-success border-success-border',
      'closed': 'bg-surface-muted text-ink-soft border-border-default'
    };
    return colors[status] || 'bg-surface-muted text-ink-soft border-border-default';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        <span className="ml-2 text-ink-muted">Loading issues...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="mx-auto h-12 w-12 text-danger mb-4" />
        <p className="text-danger mb-4">{error}</p>
        <button
          onClick={loadIssues}
          className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Ensure issues is always an array before rendering
  const safeIssues = Array.isArray(issues) ? issues : [];

  if (safeIssues.length === 0) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="mx-auto h-12 w-12 text-ink-muted mb-4" />
        <h3 className="text-lg font-medium text-ink-soft mb-2">No Issues Reported</h3>
        <p className="text-ink-muted mb-4">
          You haven't reported any issues for this vehicle yet.
        </p>
        <p className="text-sm text-ink-muted">
          Use the "Report Issue" button above to log any problems you encounter.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-ink-soft">Vehicle Issues</h3>
        <div className="text-sm text-ink-muted">
          {safeIssues.length} issue{safeIssues.length !== 1 ? 's' : ''} total
        </div>
      </div>

      {/* Issues List */}
      <div className="space-y-3">
        {safeIssues.map((issue) => (
          <div
            key={issue.id}
            className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-3 sm:p-4 transition-colors overflow-hidden hover:border-border-strong"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <h4 className="font-medium text-ink-soft">{issue.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-small font-medium border ${getSeverityColor(issue.severity)}`}>
                    {issue.severity}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-small font-medium border ${getStatusColor(issue.status)}`}>
                    {getStatusIcon(issue.status)}
                    <span className="ml-1">{issue.status.replace('_', ' ')}</span>
                  </span>
                </div>
                
                <p className="text-sm text-ink-muted mb-2">{issue.description}</p>
                
                <div className="flex items-center space-x-4 text-small text-ink-muted">
                  {issue.current_mileage && (
                    <span>Mileage: {issue.current_mileage.toLocaleString()}</span>
                  )}
                  {issue.category && (
                    <span>Category: {issue.category}</span>
                  )}
                  {issue.repair_cost && (
                    <span className="font-medium text-success">Cost: ${parseFloat(issue.repair_cost).toFixed(2)}</span>
                  )}
                  <span>Reported: {new Date(issue.created_at).toLocaleDateString()}</span>
                </div>

                {issue.ai_diagnosis && (
                  <div className="mt-3 p-3 bg-info-tint rounded-lg border border-info-border">
                    <h5 className="text-sm font-medium text-info mb-2">AI Diagnosis</h5>
                    <div className="text-small text-info">
                      {issue.ai_diagnosis.potential_causes && (
                        <div className="mb-2">
                          <strong>Potential causes:</strong> {issue.ai_diagnosis.potential_causes.join(', ')}
                        </div>
                      )}
                      {issue.ai_diagnosis.recommended_actions && (
                        <div>
                          <strong>Recommended actions:</strong> {issue.ai_diagnosis.recommended_actions.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Action buttons - responsive layout */}
              <div className="flex flex-wrap items-center gap-1.5 mt-3 sm:mt-0 sm:ml-4">
                <button
                  onClick={() => setFindingMechanicIssue(issue)}
                  className="px-2 py-1 text-small bg-accent text-white rounded hover:bg-accent-hover transition flex items-center gap-1"
                  title="Find a mechanic for this issue"
                >
                  <MapPin className="w-3 h-3" />
                  <span>Mechanic</span>
                </button>
                
                <button
                  onClick={() => setEstimateIssue(issue)}
                  className="px-2 py-1 text-small bg-info text-white rounded hover:bg-info/80 transition flex items-center gap-1"
                  title="Get AI estimate for this issue"
                >
                  <Calculator className="w-3 h-3" />
                  <span>Estimate</span>
                </button>
                
                <button
                  onClick={() => {
                    setSelectedIssue(issue);
                    setShowEditForm(true);
                  }}
                  className="p-1.5 text-ink-muted hover:text-accent transition-colors rounded hover:bg-surface-muted"
                  title="Edit issue"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteIssue(issue.id)}
                  className="p-1.5 text-ink-muted hover:text-danger transition-colors rounded hover:bg-surface-muted"
                  title="Delete issue"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Form Modal */}
      {showEditForm && selectedIssue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-card rounded-card shadow-elevation-1 p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-ink-soft mb-4">Edit Issue</h3>
            <IssueEditForm
              issue={selectedIssue}
              onSave={handleUpdateIssue}
              onCancel={() => {
                setShowEditForm(false);
                setSelectedIssue(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Find Mechanic Modal */}
      {findingMechanicIssue && (
        <FindMechanicModal
          issue={findingMechanicIssue}
          vehicleInfo={vehicleInfo}
          onClose={() => setFindingMechanicIssue(null)}
        />
      )}

      {/* Get Estimate Modal */}
      {estimateIssue && (
        <EstimateModal
          issue={estimateIssue}
          vehicleInfo={vehicleInfo}
          onClose={() => setEstimateIssue(null)}
        />
      )}
    </div>
  );
};

// Simple edit form component
const IssueEditForm = ({ issue, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: issue.title,
    description: issue.description,
    severity: issue.severity,
    status: issue.status,
    resolution_notes: issue.resolution_notes || '',
    repair_cost: issue.repair_cost || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert repair_cost to number if it's not empty
    const updates = {
      ...formData,
      repair_cost: formData.repair_cost ? parseFloat(formData.repair_cost) : null
    };
    onSave(updates);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-ink-soft mb-1">Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-ink-soft mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          rows={3}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-1">Severity</label>
          <select
            value={formData.severity}
            onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
            className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-soft mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-ink-soft mb-1">Repair Cost ($)</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formData.repair_cost}
          onChange={(e) => setFormData({ ...formData, repair_cost: e.target.value })}
          className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          placeholder="0.00"
        />
        <p className="text-small text-ink-muted mt-1">Enter the estimated or actual repair cost</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-ink-soft mb-1">Resolution Notes</label>
        <textarea
          value={formData.resolution_notes}
          onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })}
          className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          rows={2}
          placeholder="Optional: Add notes about how this issue was resolved..."
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-ink-soft bg-surface-card border border-border-default rounded-lg hover:bg-surface-page"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
        >
          Save Changes
        </button>
      </div>
    </form>
  );
};

// Find Mechanic Modal Component
function FindMechanicModal({ issue, vehicleInfo, onClose }) {
  const [loading, setLoading] = useState(false);
  const [mechanics, setMechanics] = useState([]);
  const [location, setLocation] = useState('');
  const [error, setError] = useState(null);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (!location.trim()) {
      setError('Please enter a location (city, zip code, or address)');
      return;
    }

    setSearching(true);
    setError(null);
    
    try {
      const response = await api.post('/maintenance/find-mechanics', {
        location: location.trim(),
        service: issue.title || issue.description,
        vehicle_make: vehicleInfo.make,
        vehicle_model: vehicleInfo.model
      });
      
      setMechanics(response.data.mechanics || []);
    } catch (err) {
      console.error('Error finding mechanics:', err);
      setError(err.response?.data?.detail || 'Failed to find mechanics. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-card rounded-card shadow-elevation-1 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-section-title font-semibold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-accent" />
            Find a Mechanic
          </h3>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink-muted transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-ink-muted mb-2">
            Issue: <span className="font-medium">{issue.title}</span>
          </p>
          {issue.description && (
            <p className="text-sm text-ink-muted mb-2">{issue.description}</p>
          )}
          <p className="text-sm text-ink-muted">
            Vehicle: {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-ink-soft mb-2">
            Enter your location
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="City, ZIP code, or address"
              className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
            />
            <button
              onClick={handleSearch}
              disabled={searching || !location.trim()}
              className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {searching ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4" />
                  Search
                </>
              )}
            </button>
          </div>
          {error && (
            <p className="mt-2 text-sm text-danger">{error}</p>
          )}
        </div>

        {mechanics.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-ink-soft">
              Found {mechanics.length} mechanic{mechanics.length !== 1 ? 's' : ''} nearby
            </h4>
            {mechanics.map((mechanic, idx) => (
              <div key={idx} className="border rounded-lg p-4 transition hover:border-border-strong">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h5 className="font-semibold text-ink-soft">{mechanic.name}</h5>
                    {mechanic.rating && (
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="w-4 h-4 text-warning fill-current" />
                        <span className="text-sm text-ink-soft">
                          {mechanic.rating} {mechanic.review_count && `(${mechanic.review_count} reviews)`}
                        </span>
                      </div>
                    )}
                  </div>
                  {mechanic.distance && (
                    <span className="text-sm text-ink-muted">{mechanic.distance}</span>
                  )}
                </div>
                
                {mechanic.address && (
                  <p className="text-sm text-ink-muted mb-2 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {mechanic.address}
                  </p>
                )}
                
                {mechanic.phone && (
                  <p className="text-sm text-ink-muted mb-2 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    {mechanic.phone}
                  </p>
                )}

                <div className="flex gap-2 mt-3">
                  {mechanic.website && (
                    <a
                      href={mechanic.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-accent hover:text-accent-hover flex items-center gap-1"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Website
                    </a>
                  )}
                  {mechanic.directions_url && (
                    <a
                      href={mechanic.directions_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-accent hover:text-accent-hover flex items-center gap-1"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                      Directions
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {mechanics.length === 0 && !searching && location && (
          <div className="text-center py-8 text-ink-muted">
            <MapPin className="w-12 h-12 mx-auto mb-3 text-ink-muted" />
            <p>No mechanics found. Try a different location.</p>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-surface-muted text-ink-soft rounded-lg hover:bg-surface-muted transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Estimate Modal Component
function EstimateModal({ issue, vehicleInfo, onClose }) {
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEstimate();
  }, []);

  const fetchEstimate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/maintenance/generate-estimate', {
        service: issue.title || issue.description,
        vehicle_year: vehicleInfo.year,
        vehicle_make: vehicleInfo.make,
        vehicle_model: vehicleInfo.model,
        vehicle_trim: vehicleInfo.trim,
        current_mileage: issue.current_mileage || vehicleInfo.mileage || 0,
        estimated_cost_low: issue.repair_cost ? issue.repair_cost * 0.8 : null,
        estimated_cost_high: issue.repair_cost ? issue.repair_cost * 1.2 : null,
        time_estimate: null
      });
      
      setEstimate(response.data);
    } catch (err) {
      console.error('Error generating estimate:', err);
      setError(err.response?.data?.detail || 'Failed to generate estimate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-card rounded-card shadow-elevation-1 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-section-title font-semibold flex items-center gap-2">
            <Calculator className="w-5 h-5 text-info" />
            Repair Estimate
          </h3>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink-muted transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-ink-muted mb-2">
            Issue: <span className="font-medium">{issue.title}</span>
          </p>
          {issue.description && (
            <p className="text-sm text-ink-muted mb-2">{issue.description}</p>
          )}
          <p className="text-sm text-ink-muted">
            Vehicle: {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
          </p>
        </div>

        {loading && (
          <div className="text-center py-12">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-info" />
            <p className="text-ink-muted">Generating AI estimate...</p>
          </div>
        )}

        {error && (
          <div className="bg-danger-tint border border-danger-border rounded-lg p-4 mb-4">
            <p className="text-danger">{error}</p>
          </div>
        )}

        {estimate && !loading && (
          <div className="space-y-4">
            <div className="bg-info-tint border border-info-border rounded-lg p-4">
              <h4 className="font-semibold text-info mb-3">Estimated Cost Breakdown</h4>
              
              {estimate.parts && estimate.parts.length > 0 && (
                <div className="mb-4">
                  <h5 className="font-medium text-ink-soft mb-2">Parts:</h5>
                  <div className="space-y-2">
                    {estimate.parts.map((part, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-ink-soft">{part.name}</span>
                        <span className="font-medium">${part.cost?.toFixed(2) || '0.00'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {estimate.labor && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-ink-soft">Labor ({estimate.labor_hours || estimate.time_estimate || 1} hours @ ${estimate.labor_rate || 100}/hr)</span>
                    <span className="font-medium">${estimate.labor?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              )}

              <div className="border-t border-info-border pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-lg text-ink-soft">Total Estimate:</span>
                  <span className="font-bold text-xl text-info">
                    ${estimate.total_cost?.toFixed(2) || estimate.estimated_total?.toFixed(2) || '0.00'}
                  </span>
                </div>
                {estimate.cost_range && (
                  <p className="text-sm text-ink-muted mt-1 text-right">
                    Range: ${estimate.cost_range.low} - ${estimate.cost_range.high}
                  </p>
                )}
              </div>
            </div>

            {estimate.notes && (
              <div className="bg-info-tint border border-info-border rounded-lg p-4">
                <h5 className="font-medium text-info mb-2">Additional Notes:</h5>
                <p className="text-sm text-info whitespace-pre-wrap">{estimate.notes}</p>
              </div>
            )}

            {estimate.warranty_info && (
              <div className="bg-success-tint border border-success-border rounded-lg p-4">
                <h5 className="font-medium text-success mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Warranty Information:
                </h5>
                <p className="text-sm text-success">{estimate.warranty_info}</p>
              </div>
            )}

            <div className="text-small text-ink-muted italic pt-2">
              * This is an AI-generated estimate. Actual costs may vary based on location, shop rates, and vehicle condition.
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-surface-muted text-ink-soft rounded-lg hover:bg-surface-muted transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default VehicleIssuesTab; 